import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import WpaLayout from './WpaLayout';

const PRIMARY = '#4CAF50';
const PRIMARY_DARK = '#388E3C';

const styles = {
  root: {
    padding: '24px 16px',
    maxWidth: 480,
    margin: '0 auto',
    boxSizing: 'border-box',
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    padding: '24px 20px',
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#444',
    marginBottom: 6,
  },
  inputWrap: {
    position: 'relative',
    marginBottom: 16,
  },
  input: {
    width: '100%',
    padding: '12px 44px 12px 14px',
    fontSize: 15,
    border: '1px solid #ddd',
    borderRadius: 10,
    outline: 'none',
    boxSizing: 'border-box',
  },
  eyeBtn: {
    position: 'absolute',
    right: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    color: '#999',
    fontSize: 18,
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
  },
  btn: {
    width: '100%',
    padding: '13px 0',
    fontSize: 16,
    fontWeight: 600,
    color: '#fff',
    background: PRIMARY,
    border: 'none',
    borderRadius: 10,
    cursor: 'pointer',
    marginTop: 4,
  },
  btnDisabled: {
    background: '#a5d6a7',
    cursor: 'not-allowed',
  },
  error: {
    background: '#fff5f5',
    color: '#c0392b',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    marginBottom: 16,
    border: '1px solid #f5c6cb',
  },
  success: {
    background: '#e8f5e9',
    color: '#2e7d32',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    marginBottom: 16,
    border: '1px solid #c8e6c9',
  },
  spinner: {
    display: 'inline-block',
    width: 18,
    height: 18,
    border: '3px solid rgba(255,255,255,0.3)',
    borderTop: '3px solid #fff',
    borderRadius: '50%',
    animation: 'wpa-spin 0.7s linear infinite',
    verticalAlign: 'middle',
    marginRight: 8,
  },
};

const css = `@keyframes wpa-spin { to { transform: rotate(360deg); } }`;

const Spinner = () => <span style={styles.spinner} />;

function EyeIcon({ show }) {
  return show ? (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zm0 12.5c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
    </svg>
  ) : (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46A11.804 11.804 0 001 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
    </svg>
  );
}

function PasswordInput({ value, onChange, placeholder, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <div style={styles.inputWrap}>
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        style={styles.input}
        placeholder={placeholder}
        autoComplete={autoComplete}
        minLength={8}
        required
      />
      <button
        type="button"
        style={styles.eyeBtn}
        onClick={() => setShow((s) => !s)}
        tabIndex={-1}
        aria-label={show ? '隱藏密碼' : '顯示密碼'}
      >
        <EyeIcon show={show} />
      </button>
    </div>
  );
}

export default function WpaChangePasswordPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { companyId } = useParams();

  const [current, setCurrent] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPw.length < 8) {
      setError(t('wpa.changePassword.errorMinLength'));
      return;
    }
    if (newPw !== confirm) {
      setError(t('wpa.changePassword.errorMismatch'));
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('wpa_token');
      const res = await fetch('/api/wpa-auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ companyId, currentPassword: current, newPassword: newPw }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        if (res.status === 400 && data.code === 'WRONG_CURRENT_PASSWORD') {
          setError(t('wpa.changePassword.errorWrongCurrent'));
        } else {
          setError(data.message || t('wpa.changePassword.errorNetwork'));
        }
        return;
      }
      setSuccess(true);
      setCurrent('');
      setNewPw('');
      setConfirm('');
    } catch {
      setError(t('wpa.changePassword.errorNetwork'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <WpaLayout title={t('wpa.changePassword.title')} leadingMode="back" onBack={() => navigate(-1)}>
      <style>{css}</style>
      <div style={styles.root}>
        <div style={styles.card}>
          {success && <div style={styles.success}>{t('wpa.changePassword.success')}</div>}
          <form onSubmit={handleSubmit}>
            {error && <div style={styles.error}>{error}</div>}

            <label style={styles.label}>{t('wpa.changePassword.currentPassword')}</label>
            <PasswordInput
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
            />

            <label style={styles.label}>{t('wpa.changePassword.newPassword')}</label>
            <PasswordInput
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              autoComplete="new-password"
            />

            <label style={styles.label}>{t('wpa.changePassword.confirmPassword')}</label>
            <PasswordInput
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
            />

            <button
              type="submit"
              disabled={loading}
              style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }}
              onMouseEnter={(ev) => { if (!loading) ev.currentTarget.style.background = PRIMARY_DARK; }}
              onMouseLeave={(ev) => { if (!loading) ev.currentTarget.style.background = PRIMARY; }}
            >
              {loading ? <><Spinner />{t('wpaLogin.verifying')}</> : t('wpa.changePassword.submit')}
            </button>
          </form>
        </div>
      </div>
    </WpaLayout>
  );
}
